/**
 * Browse Payout Offers Page
 * Lead Owners browse DSA commission offers, filter by criteria, and invite DSAs to their leads
 *
 * Features:
 * - Search and filter DSA offers by bank, loan type, location, commission range
 * - Grid/List view toggle
 * - Invite DSA to marketplace lead via modal
 * - Backend pagination with URL state management
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { Paginator } from 'primereact/paginator';
import { AutoComplete } from 'primereact/autocomplete';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { dsaOfferService } from '../../services/dsaOfferService';
import axiosInstance from '../../config/axios.config';
import DsaOfferCard from './components/DsaOfferCard';
import InviteToLeadDialog from './components/InviteToLeadDialog';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// Market Payout section tabs
const MARKET_PAYOUT_TABS = [
    { label: 'Discover Payout', icon: 'pi pi-search', route: '/app/dsa-offers' },
    { label: 'My Enquiries', icon: 'pi pi-send', route: '/app/dsa-offers/sent-invites' },
    { label: 'Post Payout', icon: 'pi pi-plus', route: '/app/dsa-offers/post' }
];


// Sort options
const SORT_OPTIONS = [
    { value: 'commission', label: 'Highest Commission' },
    { value: 'newest', label: 'Newest First' },
    { value: 'deadline', label: 'Earliest Deadline' },
    { value: 'views', label: 'Most Viewed' }
];

const BrowseOffers = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const searchTimeoutRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();

    // Track previous filter values to detect changes and prevent double API calls
    const prevFiltersRef = useRef(null);
    // Track last fetch key to prevent duplicate API calls (React StrictMode, etc.)
    const lastFetchKeyRef = useRef('');

    // Get initial values from URL params
    const getInitialValue = (key, defaultValue) => {
        const value = searchParams.get(key);
        if (value === null || value === '') return defaultValue;
        if (key === 'page' || key === 'per_page') return parseInt(value, 10) || defaultValue;
        return value;
    };

    // State
    const [loading, setLoading] = useState(true);
    const [offers, setOffers] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState((getInitialValue('page', 1) - 1) * 12); // For paginator
    const [perPage] = useState(8);

    // Filter states - initialized from URL
    const [searchQuery, setSearchQuery] = useState(getInitialValue('search', ''));
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(getInitialValue('search', ''));
    const [sortBy, setSortBy] = useState(getInitialValue('sort_by', 'commission'));
    const [loanType, setLoanType] = useState(getInitialValue('loan_type', null));
    const [bankName, setBankName] = useState(getInitialValue('bank', ''));
    const [debouncedBankName, setDebouncedBankName] = useState(getInitialValue('bank', ''));
    const [bankSuggestions, setBankSuggestions] = useState([]);
    const [state, setState] = useState(getInitialValue('state', null));
    const [city, setCity] = useState(getInitialValue('city', ''));
    const [debouncedCity, setDebouncedCity] = useState(getInitialValue('city', ''));
    const [viewMode, setViewMode] = useState('grid');

    // Invite dialog state
    const [showInviteDialog, setShowInviteDialog] = useState(false);
    const [selectedOfferForInvite, setSelectedOfferForInvite] = useState(null);

    // Dynamic master options (same canonical sources the Post-Offer form
    // uses — no hardcoded state / loan-type / city lists).
    const [stateOptionsRaw, setStateOptionsRaw] = useState([]);       // [{label,value}]
    const [cityOptionsRaw, setCityOptionsRaw] = useState([]);         // [{label,value,state}]
    const [loanTypeOptionsRaw, setLoanTypeOptionsRaw] = useState([]); // [{id,label,...}]
    const [loadingCities, setLoadingCities] = useState(false);

    // Build the filter Dropdown options with an "All …" lead entry.
    const stateOptions = React.useMemo(
        () => [{ label: 'All States', value: null }, ...stateOptionsRaw],
        [stateOptionsRaw]
    );
    const loanTypeOptions = React.useMemo(
        () => [{ label: 'All Types', value: null }, ...loanTypeOptionsRaw.map(lt => ({ label: lt.label, value: lt.label }))],
        [loanTypeOptionsRaw]
    );
    const cityOptions = React.useMemo(
        () => [{ label: 'All Cities', value: '' }, ...cityOptionsRaw],
        [cityOptionsRaw]
    );

    // Mount loaders — independent (no Promise.all cascade so one failure
    // never blanks the others).
    useEffect(() => {
        let cancelled = false;
        dsaOfferService.getLoanTypes()
            .then((res) => { if (!cancelled && res?.success) setLoanTypeOptionsRaw(res.data || []); })
            .catch((e) => console.error('Failed to load loan types:', e?.message || 'error'));
        axiosInstance.get('/location/states')
            .then((res) => { if (!cancelled && res?.data?.success) setStateOptionsRaw(res.data.data || []); })
            .catch((e) => console.error('Failed to load states:', e?.message || 'error'));
        return () => { cancelled = true; };
    }, []);

    // Cascading cities for the chosen state.
    const loadCities = useCallback(async (selectedState) => {
        if (!selectedState) { setCityOptionsRaw([]); return; }
        setLoadingCities(true);
        try {
            const res = await axiosInstance.get(`/location/cities/by-state/${encodeURIComponent(selectedState)}`);
            setCityOptionsRaw(res?.data?.success ? (res.data.data || []) : []);
        } catch {
            setCityOptionsRaw([]);
        } finally {
            setLoadingCities(false);
        }
    }, []);

    // When the user changes the state filter (or arrives with one from URL),
    // (re)load the city list — and clear city if it no longer belongs.
    useEffect(() => {
        if (state) loadCities(state); else setCityOptionsRaw([]);
    }, [state, loadCities]);

    const viewOptions = [
        { icon: 'pi pi-th-large', value: 'grid' },
        { icon: 'pi pi-list', value: 'list' }
    ];

    // Debounce text inputs (search, city, bank) - only updates debounced values
    // Page reset is handled in the main fetch effect
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setDebouncedCity(city);
            setDebouncedBankName(bankName);
        }, 500); // 500ms debounce for better UX

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, city, bankName]);

    // Separate effect for URL sync — does NOT trigger fetches
    useEffect(() => {
        const currentPage = Math.floor(first / perPage) + 1;
        const params = new URLSearchParams();
        if (currentPage > 1) params.set('page', currentPage.toString());
        if (debouncedSearchQuery) params.set('search', debouncedSearchQuery);
        if (sortBy && sortBy !== 'commission') params.set('sort_by', sortBy);
        if (loanType) params.set('loan_type', loanType);
        if (debouncedBankName) params.set('bank', debouncedBankName);
        if (state) params.set('state', state);
        if (debouncedCity) params.set('city', debouncedCity);
        setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, loanType, debouncedBankName, state, debouncedCity, debouncedSearchQuery, first]);

    // Single effect for fetching — separated from URL update to prevent re-render loops
    useEffect(() => {
        const currentFilters = {
            sortBy,
            loanType,
            debouncedBankName,
            state,
            debouncedCity,
            debouncedSearchQuery
        };

        const prevFilters = prevFiltersRef.current;

        // Check if any filter changed (not just pagination)
        const filtersChanged = prevFilters !== null && (
            prevFilters.sortBy !== sortBy ||
            prevFilters.loanType !== loanType ||
            prevFilters.debouncedBankName !== debouncedBankName ||
            prevFilters.state !== state ||
            prevFilters.debouncedCity !== debouncedCity ||
            prevFilters.debouncedSearchQuery !== debouncedSearchQuery
        );

        // If filters changed and we're not on first page, reset page and skip this fetch
        if (filtersChanged && first !== 0) {
            prevFiltersRef.current = currentFilters;
            setFirst(0);
            return;
        }

        // Create a unique fetch key to prevent duplicate API calls
        const fetchKey = JSON.stringify({
            sortBy, loanType, debouncedBankName, state, debouncedCity, debouncedSearchQuery, first
        });

        // Skip if we already fetched with these exact parameters
        if (fetchKey === lastFetchKeyRef.current) {
            return;
        }

        // Update refs
        prevFiltersRef.current = currentFilters;
        lastFetchKeyRef.current = fetchKey;

        // Calculate page to fetch
        const currentPage = Math.floor(first / perPage) + 1;

        // Fetch offers
        const fetchOffers = async () => {
            setLoading(true);
            try {
                const filters = {
                    sort_by: sortBy,
                    per_page: perPage,
                    page: currentPage
                };

                if (debouncedSearchQuery) filters.search = debouncedSearchQuery;
                if (loanType) filters.loan_type = loanType;
                if (debouncedBankName) filters.bank_name = debouncedBankName;
                if (state) filters.state = state;
                if (debouncedCity) filters.city = debouncedCity;

                const response = await dsaOfferService.browseOffers(filters);

                if (response.success) {
                    const data = response.data || {};
                    const offersData = data.offers || data.data || [];
                    const total = data.pagination?.total || data.total || offersData.length;

                    setOffers(offersData);
                    setTotalRecords(total);
                }
            } catch (error) {
                console.error('Failed to fetch offers:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load DSA offers'
                });
            } finally {
                setLoading(false);
            }
        };

        fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, loanType, debouncedBankName, state, debouncedCity, perPage, debouncedSearchQuery, first]);

    // Handle page change
    const onPageChange = (event) => {
        setFirst(event.first);
    };

    // Manual refresh function (for refresh button)
    const handleRefresh = async () => {
        setLoading(true);
        try {
            const currentPage = Math.floor(first / perPage) + 1;
            const filters = {
                sort_by: sortBy,
                per_page: perPage,
                page: currentPage
            };

            if (debouncedSearchQuery) filters.search = debouncedSearchQuery;
            if (loanType) filters.loan_type = loanType;
            if (debouncedBankName) filters.bank_name = debouncedBankName;
            if (state) filters.state = state;
            if (debouncedCity) filters.city = debouncedCity;

            const response = await dsaOfferService.browseOffers(filters);

            if (response.success) {
                const data = response.data || {};
                const offersData = data.offers || data.data || [];
                const total = data.pagination?.total || data.total || offersData.length;

                setOffers(offersData);
                setTotalRecords(total);
            }
        } catch (error) {
            console.error('Failed to fetch offers:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load DSA offers'
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle filter change - page reset is handled in the main fetch effect
    const handleFilterChange = (setter, value) => {
        setter(value);
        // Note: Page reset to first is handled automatically in the main useEffect
        // when it detects filter changes
    };

    // Clear filters - page reset is handled by the main fetch effect
    const clearFilters = () => {
        setSearchQuery('');
        setDebouncedSearchQuery('');
        setSortBy('commission');
        setLoanType(null);
        setBankName('');
        setDebouncedBankName('');
        setState(null);
        setCity('');
        setDebouncedCity('');
        // Note: Page reset is handled automatically in useEffect when filters change
    };

    // Bank autocomplete — hits the lenders master on the backend (same source
    // PostOffer uses). Debounced via AutoComplete's native `delay` prop. The
    // filter still uses bank NAME (the server still does LIKE matching on
    // bank_name text), so we keep `bankName` as a string and capture either
    // the typed query or the selected bank's name.
    const searchBanks = useCallback(async (event) => {
        try {
            const res = await dsaOfferService.getBankSuggestions(event?.query || '');
            const data = res?.success ? (res.data || []) : [];
            // AutoComplete suggestions need a string field to display. Use
            // bank_name from each lender row.
            setBankSuggestions(data.map((l) => l.bank_name).filter(Boolean));
        } catch (e) {
            console.error('Bank search failed:', e?.message || 'error');
            setBankSuggestions([]);
        }
    }, []);

    // Use debounced values for filter count (shows actual applied filters)
    const activeFiltersCount = [
        debouncedSearchQuery,
        loanType,
        debouncedBankName,
        state,
        debouncedCity
    ].filter(Boolean).length;

    // Handle invite click
    const handleInviteClick = (offer) => {
        setSelectedOfferForInvite(offer);
        setShowInviteDialog(true);
    };

    // Handle invite success
    const handleInviteSuccess = () => {
        setShowInviteDialog(false);
        setSelectedOfferForInvite(null);
        toast.current?.show({
            severity: 'success',
            summary: 'Enquiry Sent!',
            detail: 'The payout will be notified of your enquiry'
        });
    };

    // ── Loading skeleton — matches the new DsaOfferCard shape ──
    const renderSkeleton = () => (
        <div className="grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="col-12 md:col-6 lg:col-4 xl:col-3 p-2">
                    <div className="surface-card border-round border-1 surface-border p-3 h-full">
                        <div className="flex align-items-center gap-2 mb-3">
                            <Skeleton width="60%" height="0.85rem" className="mb-1" />
                            <Skeleton width="20%" height="0.6rem" className="ml-auto" />
                        </div>
                        <Skeleton width="100%" height="1rem" className="mb-2" />
                        <Skeleton width="80%" height="0.7rem" className="mb-3" />
                        <Skeleton width="100%" height="3rem" className="mb-2" />
                        <Skeleton width="100%" height="0.5rem" className="mb-3" />
                        <div className="flex gap-2">
                            <Skeleton width="50%" height="1.75rem" />
                            <Skeleton width="50%" height="1.75rem" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    // ── Empty state ──
    const renderEmpty = () => (
        <div className="flex flex-column align-items-center justify-content-center py-6">
            <i className="pi pi-search text-400 pi-2x mb-3" aria-hidden="true" />
            <h4 className="text-900 font-medium m-0 mb-1 text-base">No DSA payouts found</h4>
            <p className="text-500 text-sm text-center mb-3" style={{ maxWidth: '320px' }}>
                {debouncedSearchQuery || activeFiltersCount > 0
                    ? 'Try adjusting your filters or search terms.'
                    : 'No payout offers are available right now. Check back later.'}
            </p>
            {(debouncedSearchQuery || activeFiltersCount > 0) && (
                <Button label="Clear filters" icon="pi pi-filter-slash" size="small" outlined severity="secondary" onClick={clearFilters} />
            )}
        </div>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <NavigationTabMenu tabs={MARKET_PAYOUT_TABS} />
                <Toast ref={toast} />

                {/* Page header — plain div per P20, NOT inside a Card */}
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-3">
                    <div>
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Discover Payouts</h1>
                        <p className="text-600 text-sm m-0">Browse DSA commission offers and enquire on the ones that fit your leads.</p>
                    </div>
                    <Button label="Post Payout" icon="pi pi-plus" size="small" onClick={() => navigate('/app/dsa-offers/post')} />
                </div>

                {/* Search + filters bar + utility row (mirrors marketplace Browse pattern) */}
                <div className="surface-card border-round shadow-1 p-3 mb-3">
                    <div className="grid">
                        <div className="col-12 md:col-6 lg:col-4">
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by DSA, bank, city..."
                                    className="w-full p-inputtext-sm"
                                />
                            </IconField>
                        </div>
                        <div className="col-6 md:col-3 lg:col-2">
                            <Dropdown
                                value={state}
                                options={stateOptions}
                                onChange={(e) => {
                                    // Cascade: changing state resets the city filter.
                                    handleFilterChange(setState, e.value);
                                    setCity('');
                                }}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="State"
                                filter
                                showClear
                                className="w-full p-inputtext-sm"
                            />
                        </div>
                        <div className="col-6 md:col-3 lg:col-2">
                            <Dropdown
                                value={city}
                                options={cityOptions}
                                onChange={(e) => handleFilterChange(setCity, e.value || '')}
                                optionLabel="label"
                                optionValue="value"
                                placeholder={loadingCities ? 'Loading…' : (state ? 'City' : 'Pick state first')}
                                disabled={!state || loadingCities}
                                filter
                                showClear
                                emptyMessage="No cities for this state"
                                className="w-full p-inputtext-sm"
                            />
                        </div>
                        <div className="col-6 md:col-3 lg:col-2">
                            <AutoComplete
                                value={bankName}
                                suggestions={bankSuggestions}
                                completeMethod={searchBanks}
                                onChange={(e) => setBankName(e.value)}
                                placeholder="Bank"
                                delay={300}
                                className="w-full"
                                inputClassName="w-full p-inputtext-sm"
                            />
                        </div>
                        <div className="col-6 md:col-3 lg:col-2">
                            <Dropdown
                                value={loanType}
                                options={loanTypeOptions}
                                onChange={(e) => handleFilterChange(setLoanType, e.value)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Loan Type"
                                showClear
                                className="w-full p-inputtext-sm"
                            />
                        </div>
                    </div>
                    <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2 mt-3 pt-2 border-top-1 surface-border">
                        <span className="text-600 text-sm">
                            {loading ? 'Loading...' : `${totalRecords} offer${totalRecords !== 1 ? 's' : ''}`}
                            {activeFiltersCount > 0 && ` · ${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''}`}
                        </span>
                        <div className="flex align-items-center gap-2 flex-wrap">
                            <Dropdown
                                value={sortBy}
                                options={SORT_OPTIONS}
                                onChange={(e) => handleFilterChange(setSortBy, e.value)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Sort"
                                className="p-inputtext-sm"
                                style={{ minWidth: '11rem' }}
                            />
                            {activeFiltersCount > 0 && (
                                <Button label="Clear" icon="pi pi-filter-slash" severity="secondary" outlined size="small" onClick={clearFilters} />
                            )}
                            <Button icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={handleRefresh} loading={loading} aria-label="Refresh" />
                            <SelectButton
                                value={viewMode}
                                options={viewOptions}
                                onChange={(e) => e.value && setViewMode(e.value)}
                                optionLabel="icon"
                                itemTemplate={(opt) => <i className={opt.icon} aria-hidden="true" />}
                                className="hidden md:inline-flex"
                            />
                        </div>
                    </div>
                </div>

                {/* Content: skeleton / empty / grid / list */}
                {loading ? renderSkeleton() : offers.length === 0 ? renderEmpty() : (
                    viewMode === 'list' ? (
                        <div className="surface-card border-round border-1 surface-border overflow-hidden mb-3">
                            {offers.map(o => (
                                <DsaOfferCard key={o.id} offer={o} viewMode="list" onInvite={handleInviteClick} />
                            ))}
                        </div>
                    ) : (
                        <div className="grid mb-3">
                            {offers.map(o => (
                                <div key={o.id} className="col-12 md:col-6 lg:col-4 xl:col-3 p-2">
                                    <DsaOfferCard offer={o} onInvite={handleInviteClick} />
                                </div>
                            ))}
                        </div>
                    )
                )}

                {/* Paginator — trust the default (P2) */}
                {totalRecords > perPage && (
                    <Paginator
                        first={first}
                        rows={perPage}
                        totalRecords={totalRecords}
                        onPageChange={onPageChange}
                        template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                        currentPageReportTemplate="{first}-{last} of {totalRecords}"
                        paginatorClassName="text-sm"
                        pageLinkSize={3}
                        className="surface-card border-round shadow-1"
                    />
                )}

                {showInviteDialog && selectedOfferForInvite && (
                    <InviteToLeadDialog
                        visible={showInviteDialog}
                        onHide={() => { setShowInviteDialog(false); setSelectedOfferForInvite(null); }}
                        dsaOffer={selectedOfferForInvite}
                        onSuccess={handleInviteSuccess}
                    />
                )}
            </div>
        </div>
    );
};

export default BrowseOffers;
